<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Nueva solicitud de vacaciones</h1>
    </div>
    <div>
      <b-card no-body class="custom-card">
        <b-card-header class="custom-card-header d-flex align-items-center">
          <h3>Datos de la solicitud</h3>
        </b-card-header>
        <b-card-body class="custom-card-body p-4">

          <!-- Paso 1: Seleccionar empleado -->
          <div class="form-group">
            <label class="label">Empleado <span class="text-danger">*</span></label>
            <SearchableDropdown
              :options="employees"
              :oldValue="form.employeeId"
              placeholder="Buscar por nombre o número de documento"
              @selected="onEmployeeChange"
            />
          </div>

          <!-- Alerta sin contrato -->
          <div v-if="noActiveContract" class="alert alert-danger">
            <i class="ri-error-warning-line mr-2"></i>
            Este empleado no tiene contrato vigente.
          </div>

          <!-- Alerta documentación incompleta -->
          <div v-if="complianceError" class="alert alert-danger">
            <i class="ri-lock-line mr-2"></i>
            <strong>Documentación incompleta.</strong> No se puede crear la solicitud.
            Documentos faltantes: {{ complianceError.join(', ') }}
          </div>

          <!-- Resumen del empleado -->
          <div v-if="balance && !noActiveContract" class="balance-summary mb-4">
            <div class="row">
              <div class="col-md-3">
                <div class="stat-card p-3 border rounded text-center">
                  <div class="stat-label text-muted small">Empresa</div>
                  <div class="stat-value font-weight-bold">{{ balance.companyName }}</div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="stat-card p-3 border rounded text-center">
                  <div class="stat-label text-muted small">Régimen laboral</div>
                  <div class="stat-value font-weight-bold">{{ balance.laborRegime }}</div>
                </div>
              </div>
              <div class="col-md-3">
                <div class="stat-card p-3 border rounded text-center">
                  <div class="stat-label text-muted small">Días pendientes</div>
                  <div class="stat-value font-weight-bold text-primary">{{ balance.totalDaysPending }}</div>
                </div>
              </div>
              <div class="col-md-3" v-if="balance.truncDays > 0">
                <div class="stat-card p-3 border rounded text-center">
                  <div class="stat-label text-muted small">Días truncos</div>
                  <div class="stat-value font-weight-bold text-secondary">{{ balance.truncDays }}</div>
                </div>
              </div>
            </div>

            <!-- Alerta períodos por vencer -->
            <div v-if="balance.hasExpiringPeriods" class="alert alert-warning mt-3">
              <i class="ri-alarm-warning-line mr-2"></i>
              Hay períodos por vencer próximamente.
            </div>
          </div>

          <!-- Paso 2: Seleccionar período -->
          <div v-if="availablePeriods.length > 0" class="form-group">
            <label class="label">Período vacacional <span class="text-danger">*</span></label>
            <div class="table-responsive">
              <table class="custom-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>INICIO PERÍODO</th>
                    <th>FIN PERÍODO</th>
                    <th>DÍAS GANADOS</th>
                    <th>DÍAS TOMADOS</th>
                    <th>DÍAS DISPONIBLES</th>
                    <th>ESTADO</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="period in availablePeriods"
                    :key="period.id"
                    :class="{ 'table-active': form.vacationPeriodId === period.id }"
                    style="cursor:pointer"
                    @click="selectPeriod(period)"
                  >
                    <td>
                      <input
                        type="radio"
                        :value="period.id"
                        v-model="form.vacationPeriodId"
                      />
                    </td>
                    <td>{{ period.startDate }}</td>
                    <td>{{ period.endDate }}</td>
                    <td>{{ period.daysEarned }}</td>
                    <td>{{ period.daysTaken }}</td>
                    <td><strong>{{ period.daysPending }}</strong></td>
                    <td>
                      <span :class="['badge', getPeriodStatusColor(period.status)]">{{ period.status }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="$v.form.vacationPeriodId.$error" class="form-error">
              Debe seleccionar un período
            </div>
          </div>

          <div v-if="balance && availablePeriods.length === 0 && !noActiveContract" class="alert alert-info">
            No hay períodos con días disponibles.
          </div>

          <!-- Paso 3: Fechas -->
          <div v-if="form.vacationPeriodId" class="row">
            <div class="col-md-4">
              <div class="form-group">
                <label class="label">Fecha de inicio <span class="text-danger">*</span></label>
                <FormDatepicker
                  v-model="form.startDate"
                  @input="calculateDays"
                />
                <div v-if="$v.form.startDate.$error" class="form-error">
                  La fecha de inicio es obligatoria
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-group">
                <label class="label">Fecha de fin <span class="text-danger">*</span></label>
                <FormDatepicker
                  v-model="form.endDate"
                  @input="calculateDays"
                />
                <div v-if="$v.form.endDate.$error" class="form-error">
                  La fecha de fin es obligatoria
                </div>
              </div>
            </div>
            <div class="col-md-4 d-flex align-items-center">
              <div v-if="calendarDays !== null" class="mt-3">
                <span v-if="calendarDays > 0" :class="['badge', daysExceedBalance ? 'badge-danger' : 'badge-green']">
                  {{ calendarDays }} días calendario
                </span>
                <div v-if="daysExceedBalance" class="text-danger small mt-1">
                  Excede los días disponibles del período ({{ selectedPeriod ? selectedPeriod.daysPending : 0 }})
                </div>
              </div>
            </div>
          </div>

          <!-- Observaciones -->
          <div v-if="form.vacationPeriodId" class="form-group">
            <label class="label">Observaciones</label>
            <b-form-textarea
              v-model="form.observations"
              rows="3"
              placeholder="Observaciones opcionales"
            />
          </div>

          <!-- Botones -->
          <div class="form-group-buttons text-right mt-4">
            <router-link :to="{ name: 'vacation-requests-list' }" class="button button-light mr-2">
              Cancelar
            </router-link>
            <button
              type="button"
              class="button button-primary"
              :disabled="!canSave"
              @click="save()"
            >
              Guardar
            </button>
          </div>

        </b-card-body>
      </b-card>
    </div>
  </div>
</template>

<script>
import { Alert, FormDatepicker, SearchableDropdown } from 'wize-admin'
import { required } from 'vuelidate/lib/validators'
import { VacationService, EmployeeService } from '@/core/services'
import { Constants } from '@/core/utils'

export default {
  name: 'VacationRequestCreate',
  components: { FormDatepicker, SearchableDropdown },
  data() {
    return {
      employees: [],
      balance: null,
      noActiveContract: false,
      complianceError: null,
      calendarDays: null,
      form: {
        employeeId: null,
        vacationPeriodId: null,
        startDate: null,
        endDate: null,
        observations: '',
      },
    }
  },
  validations: {
    form: {
      employeeId: { required },
      vacationPeriodId: { required },
      startDate: { required },
      endDate: { required },
    },
  },
  computed: {
    availablePeriods() {
      if (!this.balance || !this.balance.periods) return []
      return this.balance.periods.filter(p => p.daysPending > 0)
    },
    selectedPeriod() {
      if (!this.form.vacationPeriodId) return null
      return this.availablePeriods.find(p => p.id === this.form.vacationPeriodId) || null
    },
    daysExceedBalance() {
      if (!this.selectedPeriod || this.calendarDays === null) return false
      return this.calendarDays > this.selectedPeriod.daysPending
    },
    canSave() {
      return (
        this.form.employeeId &&
        !this.complianceError &&
        this.form.vacationPeriodId &&
        this.form.startDate &&
        this.form.endDate &&
        this.calendarDays > 0 &&
        !this.daysExceedBalance
      )
    },
  },
  methods: {
    async onEmployeeChange(employeeId) {
      this.form.employeeId = employeeId
      this.balance = null
      this.noActiveContract = false
      this.complianceError = null
      this.form.vacationPeriodId = null
      this.form.startDate = null
      this.form.endDate = null
      this.calendarDays = null
      if (!employeeId) return
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await VacationService.getBalance(employeeId)
        this.balance = resp.payload
        if (!this.balance || !this.balance.contractId) {
          this.noActiveContract = true
          this.balance = null
        }
        try {
          const compResp = await EmployeeService.getCompliance(employeeId)
          const p = compResp.payload
          this.complianceError = p.compliant ? null : p.missingDocuments
        } catch (e) { /* non-critical */ }
      } catch (e) {
        this.noActiveContract = true
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    selectPeriod(period) {
      this.form.vacationPeriodId = period.id
      this.form.startDate = null
      this.form.endDate = null
      this.calendarDays = null
    },
    calculateDays() {
      if (!this.form.startDate || !this.form.endDate) {
        this.calendarDays = null
        return
      }
      const start = new Date(this.form.startDate)
      const end = new Date(this.form.endDate)
      const diff = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
      this.calendarDays = diff > 0 ? diff : null
    },
    getPeriodStatusColor(status) {
      const found = Object.values(Constants.VacationPeriodStatus).find(s => s.key === status)
      return found ? found.color : 'badge-gray'
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$error || !this.canSave) return
      try {
        this.$store.dispatch('app/loading', true)
        await VacationService.createRequest({
          employeeId: this.form.employeeId,
          vacationPeriodId: this.form.vacationPeriodId,
          startDate: this.form.startDate,
          endDate: this.form.endDate,
          observations: this.form.observations || null,
        })
        Alert.success('Solicitud creada correctamente')
        this.$router.push({ name: 'vacation-requests-list' })
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al crear la solicitud')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    // Si viene con employeeId en query (desde ficha empleado)
    if (this.$route.query.employeeId) {
      this.form.employeeId = Number(this.$route.query.employeeId)
      this.onEmployeeChange(this.form.employeeId)
    }
    try {
      const resp = await EmployeeService.listAll()
      const list = resp.payload || []
      this.employees = list.map(e => ({
        key: e.id,
        label: e.description,
      }))
    } catch (e) {
      console.error(e)
    }
  },
}
</script>
